import React, { useState, useEffect, useCallback } from 'react';
import Swal from '../../theme/swal';
import SectionCard from '../../components/SectionCard';
import PersonFormModal, { LEVELS } from './PersonFormModal';
import { TableSkeleton } from '../../components/Skeleton';
import { IcPlus, IcUserPlus, IcPencil, IcTrash, IcUser, IcUsers, IcClose, IcToggleOn, IcToggleOff, IcHistory } from '../../theme/icons';
import sellerIdentityService from '../../services/sellerIdentityService';
import KI from '../../theme/palette';

// Paramètres › Personnes (compte propriétaire) — les PERSONNES de l'équipe, à ne
// pas confondre avec les comptes : une personne n'a ni e-mail ni mot de passe et
// ne se connecte jamais. Elle se fait reconnaître (code tapé au clavier)
// quand elle encaisse, ajoute une facture, etc. — quel que soit le compte
// ouvert sur l'écran. Même patron visuel que Paramètres › Comptes (UserList).

const PeopleList = () => {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);  // { person, preset } | null — fiche ouverte (PersonFormModal)
  const [journal, setJournal] = useState(null);  // { person, rows } | null

  const fetchPeople = useCallback(async () => {
    try {
      setLoading(true);
      setPeople(await sellerIdentityService.listPeople());
      setError(null);
    } catch (_) {
      setError('Impossible de charger les personnes.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchPeople(); }, [fetchPeople]);

  const openForm = (person, preset) => setEditing({ person: person || null, preset: preset || null });

  const toggleActive = async (person) => {
    try {
      await sellerIdentityService.savePerson({ id: person.id, is_active: !person.is_active });
      setPeople((prev) => prev.map((p) => (p.id === person.id ? { ...p, is_active: !p.is_active } : p)));
    } catch (_) {
      Swal.fire('Erreur', 'Impossible de modifier cette personne.', 'error');
    }
  };

  const remove = async (person) => {
    const res = await Swal.fire({
      title: 'Supprimer cette personne ?',
      html: `<b>${person.name}</b><br><span style="color:${KI.faint}">Ses ventes gardent son nom.</span>`,
      icon: 'warning', showCancelButton: true, confirmButtonColor: KI.coralDark,
      confirmButtonText: 'Oui, supprimer', cancelButtonText: 'Annuler',
    });
    if (!res.isConfirmed) return;
    try {
      await sellerIdentityService.deletePerson(person.id);
      setPeople((prev) => prev.filter((p) => p.id !== person.id));
    } catch (_) {
      Swal.fire('Erreur', 'Impossible de supprimer cette personne.', 'error');
    }
  };

  const openJournal = async (person) => {
    setJournal({ person, rows: null });
    try {
      setJournal({ person, rows: await sellerIdentityService.listActions({ person: person.id, limit: 100 }) });
    } catch (_) {
      setJournal({ person, rows: [] });
    }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch (_) { return iso; } };

  const listContent = loading ? (
    <TableSkeleton rows={4} columns={[{ label: 'Personne', w: 260 }, { label: 'Niveau', w: 80 }, { label: 'Boutique', w: 100 }, { label: 'Reconnaissance', w: 160 }, { label: 'Actions', align: 'right', w: 60 }]} />
  ) : people.length === 0 ? (
    <div className="empty-ki rounded-2xl p-12 m-6">
      <IcUsers size={48} className="empty-ki-icon w-12 h-12 mb-4" />
      <p className="empty-ki-title text-lg mt-0">Aucune personne</p>
      <p className="empty-ki-sub text-sm mt-1 mb-0">Ajoutez les personnes de l’équipe : un nom et un code suffisent. Tant qu’il n’y en a aucune, la caisse ne demande rien.</p>
    </div>
  ) : (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr>
            <th scope="col" className="th-ki px-5 py-3 text-left uppercase">Personne</th>
            <th scope="col" className="th-ki px-5 py-3 text-left uppercase w-32">Niveau</th>
            <th scope="col" className="th-ki px-5 py-3 text-left uppercase w-44">Boutique</th>
            <th scope="col" className="th-ki px-5 py-3 text-left uppercase w-64">Reconnaissance</th>
            <th scope="col" className="th-ki px-5 py-3 text-right uppercase w-72">Actions</th>
          </tr>
        </thead>
        <tbody>
          {people.map((person) => {
            const level = LEVELS[person.level] || LEVELS.seller;
            return (
              <tr key={person.id} className="border-t border-gray-100 hover:bg-gray-100 transition-colors">
                <td className="td-ki px-5 py-3 text-sm">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-9 w-9 shrink-0 rounded-full bg-gray-100 text-black flex items-center justify-center"><IcUser size={16} /></div>
                    <p className="font-semibold text-gray-900 leading-tight truncate">{person.name}</p>
                  </div>
                </td>
                <td className="td-ki px-5 py-3 text-sm whitespace-nowrap"><span className={`bdg ${level.badge}`}>{level.label}</span></td>
                <td className="td-ki px-5 py-3 text-sm whitespace-nowrap text-gray-700">
                  {person.level === 'owner' ? <span className="text-gray-400">Toutes</span> : ((person.store_names || []).join(', ') || <span className="bdg bdg-warn">Sans boutique</span>)}
                </td>
                <td className="td-ki px-5 py-3 text-sm whitespace-nowrap">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`bdg ${person.is_active ? 'bdg-pos' : 'bdg-neutral'}`}>{person.is_active ? '● Active' : '○ Inactive'}</span>
                    <span className={`bdg ${person.has_pin ? 'bdg-pos' : 'bdg-neutral'}`}>{person.has_pin ? 'Code' : 'Sans code'}</span>
                  </div>
                </td>
                <td className="td-ki px-5 py-3 text-sm whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <button type="button" onClick={() => openForm(person)} className="btn-g-edit gap-2 px-3 py-1.5 text-sm transition-colors"><IcPencil /> Modifier</button>
                    <button type="button" onClick={() => openJournal(person)} title="Ce qu’elle a fait" aria-label={`Actions de ${person.name}`} className="h-9 w-9 flex items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"><IcHistory /></button>
                    <button type="button" onClick={() => toggleActive(person)} title={person.is_active ? 'Désactiver' : 'Activer'} aria-label={person.is_active ? 'Désactiver la personne' : 'Activer la personne'} className={`h-9 w-9 flex items-center justify-center rounded-lg transition-colors ${person.is_active ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}>
                      {person.is_active ? <IcToggleOn className="text-lg" /> : <IcToggleOff className="text-lg" />}
                    </button>
                    <button type="button" onClick={() => remove(person)} title="Supprimer" aria-label="Supprimer la personne" className="h-9 w-9 flex items-center justify-center rounded-lg text-red-600 hover:bg-red-50 transition-colors"><IcTrash /></button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4" role="alert">{error}</div>}

      <SectionCard
        title="Personnes"
        sub="Qui se sert des comptes : un nom + un code. Pas d’e-mail, pas de mot de passe."
        flush
        action={!loading && (
          <button
            type="button"
            onClick={() => openForm(null)}
            className="h-9 w-9 flex items-center justify-center btn-mini transition-colors shrink-0"
            title="Ajouter une personne"
            aria-label="Ajouter une personne"
          >
            <IcPlus />
          </button>
        )}
      >
        {listContent}
      </SectionCard>

      {editing && (
        <PersonFormModal
          key={editing.person ? editing.person.id : 'new'}
          person={editing.person}
          preset={editing.preset}
          onSaved={() => { setEditing(null); fetchPeople(); }}
          onClose={() => setEditing(null)}
        />
      )}

      {journal && (
        <div className="modal-backdrop z-50 flex items-start justify-center p-4 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) setJournal(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8 overflow-hidden">
            <div className="modal-head p-5">
              <h3 className="modal-title text-lg flex items-center gap-2"><IcHistory /> Ce qu’a fait {journal.person.name}</h3>
              <button type="button" onClick={() => setJournal(null)} className="text-gray-400 hover:text-white" aria-label="Fermer"><IcClose /></button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {journal.rows === null ? <p className="p-6 text-sm text-gray-400">Chargement…</p>
                : journal.rows.length === 0 ? <p className="p-6 text-sm text-gray-400">Aucune action enregistrée pour l’instant.</p>
                : (
                  <table className="min-w-full">
                    <thead><tr>
                      <th scope="col" className="th-ki px-5 py-3 text-left uppercase w-44">Date</th>
                      <th scope="col" className="th-ki px-5 py-3 text-left uppercase">Action</th>
                      <th scope="col" className="th-ki px-5 py-3 text-left uppercase w-32">Compte</th>
                    </tr></thead>
                    <tbody>
                      {journal.rows.map((a) => (
                        <tr key={a.id} className="border-t border-gray-100">
                          <td className="td-ki px-5 py-3 text-sm text-gray-600 whitespace-nowrap tabular-nums">{fmt(a.date)}</td>
                          <td className="td-ki px-5 py-3 text-sm text-gray-900">{a.label}</td>
                          <td className="td-ki px-5 py-3 text-sm text-gray-600 whitespace-nowrap">{a.account_name || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </div>
            <div className="px-5 py-3 border-t border-gray-100 flex justify-end">
              <button type="button" onClick={() => setJournal(null)} className="btn-g px-5 py-2.5 text-sm">Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeopleList;
